/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/addons/addon_serial_helpers.test.ts
 *
 * Generic helpers that surface a device's `Serial` component (`serial:N`)
 * as an addon peripheral entry.
 *
 */

//Hooks
import { get } from 'svelte/store';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
//Formatters
import { format } from 'svelte-i18n';
//Helpers
import { getSerialComponent, real_id } from '../interface_helpers';
//Stores
import { devices_status } from 'src/stores/device-runtime/status';
//Types
import type { Device } from 'src/types/Device';
import type { Peripheral, PeripheralSettings, Sensor } from 'src/types/Peripherals';
import type { SerialConfig, SerialMode } from 'src/types/SerialConfig';

const SERIAL_ICON = 'fal fa-arrow-down-arrow-up';

export const SERIAL_MODES_MAP: Record<string, string> = {
	disabled: 'disabled',
	jsUart: 'js_uart',
	mbServer: 'mb_server',
	mbClient: 'mb_client',
	// shellyProtocol: 'shelly',
};

/**
 * Catalog of friendly labels per known `serial.mode` value.
 *
 * - `js_uart` is the canonical UART value reported by both Pill and other
 *   devices with Serial support.
 * - `mb_client` / `mb_server` are the two Modbus RTU variants;
 *
 */
export const SERIAL_MODE_LABELS: Record<string, { key: string; default: string }> = {
	[SERIAL_MODES_MAP.disabled]: { key: 'option_disabled', default: 'Disabled' },
	[SERIAL_MODES_MAP.jsUart]: { key: 'JS UART', default: 'JS UART' },
	[SERIAL_MODES_MAP.mbClient]: {
		key: 'label_serial_mode_modbus_rtu_client',
		default: 'Modbus RTU Client',
	},
	[SERIAL_MODES_MAP.mbServer]: {
		key: 'label_serial_mode_modbus_rtu_server',
		default: 'Modbus RTU Server',
	},
	// [SERIAL_MODES_MAP.shellyProtocol]: { key: 'label_serial_mode_shelly_protocol', default: 'Shelly Serial Protocol' },
};

export const SERIAL_MODE_ICONS: Record<string, string> = {
	[SERIAL_MODES_MAP.disabled]: 'fal fa-circle-pause',
	[SERIAL_MODES_MAP.jsUart]: 'fal fa-terminal',
	[SERIAL_MODES_MAP.mbClient]: 'fal fa-arrow-right-arrow-left',
	[SERIAL_MODES_MAP.mbServer]: 'fal fa-server',
};

/**
 * Resolve the translated display label for a `serial.mode` value.
 * Falls back to the raw string when the value is not in the catalog.
 */
export const getSerialModeLabel = (mode: SerialMode | undefined): string => {
	if (!mode) return '';
	const entry = SERIAL_MODE_LABELS[mode];
	const $format = get(format);
	return entry ? $format(entry.key, { default: entry.default }) : String(mode);
};

/**
 * Build the label list for a Serial peripheral advanced card
 * (e.g. `"Modbus RTU Client"`) plus a compact line-settings summary
 * (e.g. `"115200 8N1"`) when baud + frame format are known, and the
 * Modbus server address when the port is configured as an RTU server.
 */
export const buildSerialPeripheralLabels = (
	peripheral: Peripheral,
): { icon?: string; value: string | number }[] => {
	const config = peripheral.settings as unknown as SerialConfig;
	const baud = config?.serial?.baud;
	const frameFormat = config?.serial?.format;
	const serverAddress = config?.mb_server?.addr;
	const labels: { icon?: string; value: string | number }[] = [
		{ icon: SERIAL_MODE_ICONS[config?.mode] ?? 'fal fa-wave-square', value: peripheral.value },
	];
	if (config?.mode !== SERIAL_MODES_MAP.disabled && Number.isFinite(baud)) {
		labels.push({ icon: 'fal fa-gauge-high', value: baud });
	}
	if (config?.mode !== SERIAL_MODES_MAP.disabled && frameFormat) {
		labels.push({ icon: 'fal fa-code', value: frameFormat });
	}
	if (config?.mode === SERIAL_MODES_MAP.mbServer && Number.isFinite(serverAddress)) {
		labels.push({ icon: 'fal fa-server', value: serverAddress });
	}
	return labels;
};

/**
 * Read the current `serial.mode` from the settings store (authoritative)
 * with a fallback to the live status payload. Returns `undefined` when
 * the component exists but the mode cannot be resolved.
 */
const readSerialMode = (
	settings: Record<string, any> | undefined,
	status: Record<string, any> | undefined,
	component: string,
): SerialMode | undefined => {
	const fromSettings = (settings?.[component] as SerialConfig | undefined)?.mode;
	if (fromSettings) return fromSettings;
	return (status?.[component] as { mode?: SerialMode } | undefined)?.mode;
};

/**
 * Returns a single addon `Peripheral` entry for the device's Serial
 * component, or leaves `interface_data` unchanged when:
 *   - the device has no `serial:N` component in its status, or
 *   - the serial mode is missing / `disabled` (functionally off).
 */
export const getSerialAddonStatus = (
	device: Device,
	interface_data: Peripheral[] = [],
	showDisabledMode = false,
): Peripheral[] => {
	const id = real_id(device?.id);
	const status = devices_status.getByRealId(id);
	const component = getSerialComponent(status);
	if (!component) return interface_data;

	const settings = readDeviceSettings(id);
	const mode = readSerialMode(settings, status, component);
	if (!mode || (!showDisabledMode && mode === 'disabled')) return interface_data;

	const $format = get(format);
	const name = $format('label_serial_port_title', { default: 'Serial port' });
	const value = getSerialModeLabel(mode);
	const extid = Number(component.split(':')[1]);
	const serialSettings = settings?.[component] || {};

	const sensor: Sensor = {
		id,
		type: 'serial',
		unit: '',
		component,
		inputType: mode,
		value,
		extid,
		alias: name,
		icon: SERIAL_ICON,
		label: name,
		name,
	};

	interface_data.push({
		name: component,
		peripheralType: 'serial',
		peripheralInputType: mode,
		icon: SERIAL_ICON,
		value,
		deviceID: id,
		sensor,
		settings: { ...serialSettings, id: extid } as unknown as PeripheralSettings,
	});

	return interface_data;
};

/**
 * The `serial.format` configuration parameter accepts a three-character string,
 * where each character defines an aspect of the serial framing:
 *
 * 1. Number of data bits: `7` or `8`
 * 2. Parity: `N`, `E`, or `O` for None, Even, or Odd
 * 3. Number of stop bits: `1`, `2`, or the letter `x` for 1.5
 *
 * @see https://shelly-api-docs.shelly.cloud/gen2/ComponentsAndServices/Serial/#configuration
 */
export const SERIAL_FORMAT_VALUES = [
	'7N1',
	'7NX',
	'7N2',
	'7E1',
	'7EX',
	'7E2',
	'7O1',
	'7OX',
	'7O2',
	'8N1',
	'8NX',
	'8N2',
	'8E1',
	'8EX',
	'8E2',
	'8O1',
	'8OX',
	'8O2',
];

export const SERIAL_BAUD_RATES = [
	600, 1200, 1800, 2400, 4800, 9600, 19200, 28800, 38400, 57600, 76800, 115200, 230400, 460800, 576000,
	921600,
];
